import { datadogLogs } from "@datadog/browser-logs";
import { ErrorBoundary, Provider } from "@rollbar/react";
import { ReactNode, useEffect, useState } from "react";
import Rollbar from "rollbar";

import { useAppSelector } from "store/index";
import { selectCurrentSectionId, selectCurrentUser } from "store/selectors";
import { shouldIgnoreUncaughtRollbarError } from "utils/api-errors";
import { getOrgIdFromCookie } from "utils/auth";

const config: Rollbar.Configuration = {
  accessToken: window._env_.ROLLBAR_CLIENT_ACCESS_TOKEN,
  captureUncaught: true,
  captureUnhandledRejections: true,
  enabled: window._env_.ROLLBAR_ENABLED === true,
  environment: window._env_.ENV,
  checkIgnore: (_isUncaught, args) => {
    const candidates: unknown[] = Array.isArray(args) ? args : [];
    return candidates.some(shouldIgnoreUncaughtRollbarError);
  },
};

// eslint-disable-next-line react-refresh/only-export-components
export const rollbar = new Rollbar(config);

window.Rollbar = rollbar;

export const LoggingProvider = ({ children }: { children: ReactNode }) => {
  const user = useAppSelector(selectCurrentUser);
  const email = user?.email;
  const sectionId = useAppSelector(selectCurrentSectionId);
  const orgId = getOrgIdFromCookie();
  const [datadogInitialized, setDatadogInitialized] = useState(false);

  useEffect(() => {
    if (!datadogInitialized && window._env_.DATADOG_ENABLED === true) {
      datadogLogs.init({
        clientToken: window._env_.DATADOG_CLIENT_TOKEN,
        env: window._env_.ENV,
        forwardErrorsToLogs: true,
        forwardConsoleLogs: "all",
        sessionSampleRate: 100,
        site: "datadoghq.com",
        service: "monarch",
        version: window._env_.COMMIT || "unknown",
      });
      setDatadogInitialized(true);
    }
  }, [datadogInitialized]);

  useEffect(() => {
    if (datadogInitialized) {
      if (user?.id) {
        datadogLogs.setGlobalContextProperty("userId", user.id);
        datadogLogs.setGlobalContextProperty("email", email);
        datadogLogs.setGlobalContextProperty("orgId", orgId);
        datadogLogs.setGlobalContextProperty("courseId", sectionId);
      } else {
        for (const key of ["userId", "email", "orgId", "courseId"] as const) {
          datadogLogs.removeGlobalContextProperty(key);
        }
      }
    }

    rollbar.configure({
      payload: {
        client: {
          javascript: {
            source_map_enabled: true,
          },
        },
        person: (user?.id
          ? { id: user.id, email }
          : { id: null, email: null }) as Rollbar.PersonParams,
        org_id: orgId,
        code_version: window._env_.COMMIT || "unknown",
      },
    });
  }, [user?.id, email, sectionId, orgId, datadogInitialized]);

  return (
    <Provider instance={rollbar}>
      <ErrorBoundary>{children}</ErrorBoundary>
    </Provider>
  );
};
